/**
 * AdminTeamPermissions — Super-admin console for granting/revoking per-user
 * capabilities on top of the baseline admin set.
 *
 * Route: /admin/team-permissions
 *
 * The baseline (church/events/schools/bookshop RW etc.) applies automatically
 * to every non-locked admin. This page is only needed to:
 *   • grant sensitive capabilities like `finance.view.totals`,
 *     `users.manage_admins`, `security.manage`, `deployment.manage`
 *   • revoke a baseline capability from a specific user
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Shield, ShieldCheck, ShieldX, Search, Save,
  Crown, Users, User,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const CAP_TONE = {
  finance: 'border-rose-500/40 bg-rose-500/10 text-rose-200',
  security: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  deployment: 'border-orange-500/40 bg-orange-500/10 text-orange-200',
  users: 'border-violet-500/40 bg-violet-500/10 text-violet-200',
  ledger: 'border-sky-500/40 bg-sky-500/10 text-sky-200',
};
const catOf = (cap) => (cap || '').split('.')[0];
const toneFor = (cap) => CAP_TONE[catOf(cap)] || 'border-slate-700 bg-slate-900 text-slate-300';

export default function AdminTeamPermissions() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const teamQ = useQuery({
    queryKey: ['team-caps'],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/team/capabilities`, { headers: authH() },
    )).data,
  });

  const filtered = useMemo(() => {
    const items = teamQ.data?.items || [];
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((a) =>
      (a.email || '').toLowerCase().includes(q) ||
      (a.name || '').toLowerCase().includes(q) ||
      (a.role || '').toLowerCase().includes(q),
    );
  }, [teamQ.data, search]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="team-permissions-page">
      <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
        <div>
          <Button
            variant="ghost" size="sm"
            className="mb-1 text-slate-400 hover:text-slate-100"
            onClick={() => navigate(-1)} data-testid="back-btn"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Shield className="h-6 w-6 text-emerald-400" /> Team Permissions
          </h1>
          <p className="text-xs text-slate-500">
            Every admin already has a safe baseline (church · events · schools · bookshop).
            Grant sensitive capabilities like <code className="text-amber-300">finance.view.totals</code>{' '}
            or <code className="text-violet-300">users.manage_admins</code> here.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="team-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search admins by email, name, or role…"
              className="pl-8 border-slate-700 bg-slate-900"
            />
          </div>
          <span className="text-xs text-slate-500">
            {filtered.length} of {(teamQ.data?.items || []).length} admins
          </span>
          <Button
            size="sm" variant="ghost"
            data-testid="open-denied-attempts"
            className="text-amber-300 hover:text-amber-200"
            onClick={() => navigate('/admin/governance/denied-attempts')}
          >
            Denied attempts
          </Button>
        </div>

        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-emerald-300" /> Admin accounts
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[70vh] overflow-y-auto">
            {teamQ.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
            <div className="space-y-1">
              {filtered.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  data-testid={`team-row-${a.id}`}
                  onClick={() => setEditing(a)}
                  className="w-full text-left rounded border border-slate-800 bg-slate-950 px-3 py-2 hover:border-emerald-500/40 hover:bg-emerald-500/5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-100">{a.name}</span>
                        {a.is_super_admin && (
                          <Badge className="border-amber-500/40 bg-amber-500/20 text-amber-200 text-[10px]">
                            <Crown className="mr-0.5 h-3 w-3" /> super
                          </Badge>
                        )}
                        {a.is_active === false && (
                          <Badge className="border-slate-600 bg-slate-800 text-slate-400 text-[10px]">
                            locked
                          </Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {a.email} · role={a.role || '—'} · {a.capabilities.length} capabilities
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap justify-end max-w-[45%]">
                      {a.granted_overrides.slice(0, 3).map((c) => (
                        <Badge key={c} className={`${toneFor(c)} text-[9px]`} data-testid={`chip-grant-${c}`}>
                          <ShieldCheck className="mr-0.5 h-2.5 w-2.5" /> +{c}
                        </Badge>
                      ))}
                      {a.revoked_overrides.slice(0, 3).map((c) => (
                        <Badge key={c} className="border-slate-600 bg-slate-800 text-slate-400 text-[9px]">
                          <ShieldX className="mr-0.5 h-2.5 w-2.5" /> -{c}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </button>
              ))}
              {filtered.length === 0 && !teamQ.isLoading && (
                <p className="p-3 text-center text-xs text-slate-500">No admins match your search.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {editing && (
          <EditCapabilitiesDialog
            admin={editing}
            allCapabilities={teamQ.data?.all_capabilities || []}
            strictCapabilities={teamQ.data?.strict_capabilities || []}
            baseline={teamQ.data?.baseline_admin || []}
            onClose={() => setEditing(null)}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['team-caps'] });
              setEditing(null);
            }}
          />
        )}
      </div>
    </div>
  );
}


function EditCapabilitiesDialog({ admin, allCapabilities, strictCapabilities = [], baseline, onClose, onSaved }) {
  const strictSet = useMemo(() => new Set(strictCapabilities), [strictCapabilities]);
  const [granted, setGranted] = useState(new Set([
    ...(admin.granted_overrides || []),
    ...(admin.flat_permissions || []).filter((p) => strictSet.has(p)),
  ]));
  const [revoked, setRevoked] = useState(new Set(admin.revoked_overrides || []));

  const save = useMutation({
    mutationFn: async () => (await axios.put(
      `${API}/api/admin/team/${admin.id}/capabilities`,
      { granted: [...granted], revoked: [...revoked] },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => { toast.success('Permissions updated'); onSaved?.(); },
    onError: (e) => toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`),
  });

  // Group capabilities by module. Strict money/security sections are merged in
  // so a General Admin can be given "read finance" while keeping "edit events".
  const grouped = useMemo(() => {
    const map = {};
    [...new Set([...allCapabilities, ...strictCapabilities])].sort().forEach((c) => {
      const [mod] = c.split('.', 1);
      (map[mod] = map[mod] || []).push(c);
    });
    return map;
  }, [allCapabilities, strictCapabilities]);

  const toggleGrant = (cap) => {
    setGranted((prev) => {
      const next = new Set(prev);
      next.has(cap) ? next.delete(cap) : next.add(cap);
      return next;
    });
    setRevoked((prev) => { const next = new Set(prev); next.delete(cap); return next; });
  };
  const toggleRevoke = (cap) => {
    setRevoked((prev) => {
      const next = new Set(prev);
      next.has(cap) ? next.delete(cap) : next.add(cap);
      return next;
    });
    setGranted((prev) => { const next = new Set(prev); next.delete(cap); return next; });
  };
  const isBaseline = (cap) => baseline.includes(cap);
  const willBeEffective = (cap) => {
    if (revoked.has(cap)) return false;
    if (granted.has(cap)) return true;
    if (admin.is_super_admin) return true;
    return isBaseline(cap);
  };
  const hasStrict = (mod) => (grouped[mod] || []).some((c) => strictSet.has(c));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="edit-caps-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-emerald-300" />
            {admin.name}
            {admin.is_super_admin && (
              <Badge className="border-amber-500/40 bg-amber-500/20 text-amber-200 text-[10px] ml-2">super</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          Baseline capabilities are enabled automatically. Toggle a capability to <span className="text-emerald-300">grant</span> or <span className="text-rose-300">revoke</span> it explicitly.
          Sections marked <span className="text-rose-300">strict</span> (money, giving, security, ledger) have no baseline —
          they open only for the exact actions you tick here, so one person can read finance while editing events.
        </p>

        <div className="mt-3 space-y-4">
          {Object.entries(grouped).map(([mod, caps]) => (
            <div key={mod} data-testid={`caps-module-${mod}`} className="rounded border border-slate-800 bg-slate-950 p-3">
              <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400">
                {mod}
                {hasStrict(mod) && (
                  <Badge className="border-rose-500/40 bg-rose-500/10 text-[9px] text-rose-200">strict · no baseline</Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {caps.map((cap) => {
                  const effective = willBeEffective(cap);
                  const isGranted = granted.has(cap);
                  const isRevoked = revoked.has(cap);
                  const isBase = isBaseline(cap);
                  return (
                    <div key={cap} className="flex items-center gap-1 rounded border border-slate-800 bg-slate-900 px-2 py-1 text-xs">
                      <span className={`font-mono ${effective ? 'text-slate-100' : 'text-slate-500'}`}>
                        {cap.replace(mod + '.', '')}
                      </span>
                      {isBase && <span className="text-[9px] text-slate-500">·base</span>}
                      <button
                        type="button"
                        data-testid={`grant-${cap}`}
                        onClick={() => toggleGrant(cap)}
                        className={`ml-1 rounded px-1 py-0.5 text-[9px] ${
                          isGranted
                            ? 'bg-emerald-500 text-slate-950'
                            : 'bg-slate-800 text-slate-400 hover:bg-emerald-500/20 hover:text-emerald-200'
                        }`}
                        title="Grant"
                      >
                        <ShieldCheck className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        data-testid={`revoke-${cap}`}
                        onClick={() => toggleRevoke(cap)}
                        className={`rounded px-1 py-0.5 text-[9px] ${
                          isRevoked
                            ? 'bg-rose-500 text-slate-950'
                            : 'bg-slate-800 text-slate-400 hover:bg-rose-500/20 hover:text-rose-200'
                        }`}
                        title="Revoke"
                      >
                        <ShieldX className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="save-caps"
            className="bg-emerald-600 hover:bg-emerald-500"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            <Save className="mr-1 h-4 w-4" />
            {save.isPending ? 'Saving…' : 'Save Permissions'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
